import { Calendar, Minus, Plus } from 'lucide-react'
import { useContext, useMemo } from 'react'

import { Badge } from 'slab'
import { getLocaleDateString } from 'utils'
import { useGetBrokersMembersMemberIdCoveredParticipants } from 'utils/web_api'
import { type BrokersWorkflow } from 'utils/web_api.schemas'

import BrokersMemberContext from '../context/BrokersMemberContext'

type WorkflowMetadataDetailsProps = {
  workflow: BrokersWorkflow
  headerVariant?: 'full' | 'compact'
}

const WorkflowMetadataDetails = ({
  workflow,
  headerVariant = 'full',
}: WorkflowMetadataDetailsProps) => {
  const { memberId, planYear } = useContext(BrokersMemberContext)

  if (!workflow.metadata) return null

  const metadata = workflow.metadata as {
    start_date?: string
    dependents_added?: string[]
    dependents_removed?: string[]
    dependent_changes_detected?: boolean
  }

  const dependentsAdded = metadata.dependents_added || []
  const dependentsRemoved = metadata.dependents_removed || []
  const hasChanges = dependentsAdded.length > 0 || dependentsRemoved.length > 0
  const hasStartDate = !!metadata.start_date

  // Only show if there are details to display
  if (!hasChanges && !hasStartDate) {
    return null
  }

  // Fetch covered participants data (only if we have changes to display)
  const { data: participants } = useGetBrokersMembersMemberIdCoveredParticipants(
    memberId || workflow.enrollment_info?.member_id || '',
    { plan_year: planYear || new Date().getFullYear(), include_pending: true },
    {
      query: {
        enabled: !!memberId && !!planYear && hasChanges,
      },
    }
  )

  // Helper function to get participant display name by ID - optimized with useMemo
  const getParticipantName = useMemo(() => {
    if (!participants) return (id: string) => `Participant ${id.slice(-6)}`

    const participantMap = new Map(
      participants.map((p) => [p.id, `${p.given_name} ${p.family_name}`])
    )

    return (id: string) =>
      participantMap.get(id) || `Participant ${id.slice(-6)}`
  }, [participants])

  const detailsHeaderText =
    headerVariant === 'full' ? 'Coverage Details:' : 'Details:'
  const changesHeaderText =
    headerVariant === 'full' ? 'Dependent Changes:' : 'Changes:'

  return (
    <div className='space-y-3'>
      {/* Two-column layout with headers: Details on left, Changes on right */}
      <div className='flex flex-col md:flex-row gap-4'>
        {/* Left column - Details */}
        {hasStartDate && (
          <div className='flex-1 space-y-2'>
            <h6 className='font-medium text-sm text-gray-600'>
              {detailsHeaderText}
            </h6>
            <div className='flex items-center space-x-2'>
              <Calendar className='w-4 h-4 text-blue-500' />
              <span className='text-sm text-gray-600'>
                Coverage starts{' '}
                {getLocaleDateString(new Date(metadata.start_date!))}
              </span>
            </div>
          </div>
        )}

        {/* Right column - Changes */}
        {hasChanges && (
          <div className='flex-1 space-y-2'>
            <h6 className='font-medium text-sm text-gray-600'>
              {changesHeaderText}
            </h6>
            <div className='flex flex-wrap gap-2'>
              {dependentsAdded.map((dependentId) => (
                <Badge
                  key={`added-${dependentId}`}
                  type='success'
                  leftIcon={Plus}
                  bordered
                >
                  {getParticipantName(dependentId)}
                </Badge>
              ))}
              {dependentsRemoved.map((dependentId) => (
                <Badge
                  key={`removed-${dependentId}`}
                  type='critical'
                  leftIcon={Minus}
                  bordered
                >
                  {getParticipantName(dependentId)}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default WorkflowMetadataDetails
